import { useState, useEffect, useCallback } from 'react'
import {
  readFlashcards,
  insertFlashcards,
  updateFlashcard,
  deleteFlashcard,
  deleteAllUserFlashcards,
} from '../db/flashcards.api.js'

/**
 * Custom hook to manage flashcards data and operations.
 * Provides loading states, error handling, and CRUD operations.
 *
 * @returns {Object} Flashcards management object
 * @returns {Array<Object>} cards - Array of flashcard objects
 * @returns {boolean} isInitialLoading - True during first load
 * @returns {boolean} isRefetching - True during subsequent refreshes
 * @returns {string|null} dataError - Error message from data operations
 * @returns {Set<string>} mutatingIds - IDs of cards currently being deleted
 * @returns {Function} prefetch - Load flashcards data
 * @returns {Function} insert - Insert new flashcards batch
 * @returns {Function} remove - Delete a single flashcard
 * @returns {Function} refetch - Reload flashcards data
 */
export default function useFlashcards() {
  // State for flashcard data
  const [cards, setCards] = useState([])

  // Loading states - differentiate initial load from refetch
  const [isInitialLoading, setIsInitialLoading] = useState(true)
  const [isRefetching, setIsRefetching] = useState(false)

  // Error state for data operations
  const [dataError, setDataError] = useState(null)

  // Track which cards are being deleted (for individual spinners)
  const [mutatingIds, setMutatingIds] = useState(new Set())

  // Track if delete all operation is in progress
  const [isDeletingAll, setIsDeletingAll] = useState(false)

  /**
   * Fetch flashcards from the database.
   * Handles both initial load and subsequent refetches.
   *
   * @param {boolean} isInitial - Whether this is the initial load
   */
  const fetchFlashcards = useCallback(async (isInitial = false) => {
    // Set appropriate loading flag
    if (isInitial) {
      setIsInitialLoading(true)
    } else {
      setIsRefetching(true)
    }

    setDataError(null)

    try {
      const { data, error } = await readFlashcards()

      if (error) {
        // Transform Supabase error to user-friendly message
        setDataError('Nie udało się pobrać fiszek. Spróbuj ponownie.')
        console.error('Error fetching flashcards:', error)
        setCards([])
      } else {
        // Successfully loaded flashcards
        setCards(data || [])
      }
    } catch (err) {
      // Handle network or unexpected errors
      setDataError('Brak połączenia z serwerem.')
      console.error('Unexpected error fetching flashcards:', err)
      setCards([])
    } finally {
      // Clear loading flags
      if (isInitial) {
        setIsInitialLoading(false)
      } else {
        setIsRefetching(false)
      }
    }
  }, [])

  /**
   * Prefetch flashcards on mount.
   * This is the initial data load.
   */
  const prefetch = useCallback(() => {
    void fetchFlashcards(true)
  }, [fetchFlashcards])

  /**
   * Refetch flashcards data.
   * Used after insert or delete operations.
   */
  const refetch = useCallback(() => {
    void fetchFlashcards(false)
  }, [fetchFlashcards])

  /**
   * Insert a batch of flashcards.
   * Automatically refetches data on success.
   *
   * @param {Array<{question: string, answer: string}>} batch - Flashcards to insert
   * @returns {Promise<{success: boolean, error: string|null}>} Operation result
   */
  const insert = useCallback(
    async (batch) => {
      // Guard: validate input
      if (!batch || !Array.isArray(batch) || batch.length === 0) {
        return {
          success: false,
          error: 'Brak prawidłowych fiszek do zaimportowania.',
        }
      }

      setDataError(null)

      try {
        const { error } = await insertFlashcards(batch)

        if (error) {
          // Check if error is due to flashcard limit trigger
          const isLimitError =
            error.message && error.message.includes('maximum limit')

          // Transform error to user-friendly message
          const errorMessage = isLimitError
            ? 'Osiągnięto limit 100 fiszek. Usuń niektóre fiszki, aby dodać nowe.'
            : 'Nie udało się zaimportować fiszek. Spróbuj ponownie.'

          setDataError(errorMessage)
          console.error('Error inserting flashcards:', error)
          return { success: false, error: errorMessage }
        }

        // Success - refetch to show new cards
        await fetchFlashcards(false)
        return { success: true, error: null }
      } catch (err) {
        // Handle network or unexpected errors
        const errorMessage = 'Brak połączenia z serwerem.'
        setDataError(errorMessage)
        console.error('Unexpected error inserting flashcards:', err)
        return { success: false, error: errorMessage }
      }
    },
    [fetchFlashcards]
  )

  /**
   * Update a single flashcard by ID.
   * Uses optimistic update for instant UI feedback.
   *
   * @param {string} id - Flashcard ID to update
   * @param {Object} updates - Updated flashcard data
   * @param {string} updates.question - Updated question text
   * @param {string} updates.answer - Updated answer text
   * @returns {Promise<{success: boolean, error: string|null}>} Operation result
   */
  const updateCard = useCallback(
    async (id, { question, answer }) => {
      // Guard: validate ID format (UUID should be 36 characters)
      if (!id || typeof id !== 'string' || id.length !== 36) {
        return {
          success: false,
          error: 'Nieprawidłowy identyfikator fiszki.',
        }
      }

      // Guard: validate question and answer are non-empty strings
      if (
        !question ||
        typeof question !== 'string' ||
        !answer ||
        typeof answer !== 'string'
      ) {
        return {
          success: false,
          error: 'Pytanie i odpowiedź nie mogą być puste.',
        }
      }

      // Store previous state for rollback
      const previousCards = cards

      // Optimistic update: immediately update local state
      setCards((prev) =>
        prev.map((card) =>
          card.id === id ? { ...card, question, answer } : card
        )
      )

      // Add ID to mutating set (for spinner/disabled state)
      setMutatingIds((prev) => new Set(prev).add(id))
      setDataError(null)

      try {
        const { error } = await updateFlashcard(id, { question, answer })

        if (error) {
          // Rollback optimistic update
          setCards(previousCards)

          // Transform error to user-friendly message
          const errorMessage =
            'Nie udało się zaktualizować fiszki. Spróbuj ponownie.'
          setDataError(errorMessage)
          console.error('Error updating flashcard:', error)

          // Remove from mutating set
          setMutatingIds((prev) => {
            const next = new Set(prev)
            next.delete(id)
            return next
          })

          return { success: false, error: errorMessage }
        }

        // Success - remove from mutating set
        setMutatingIds((prev) => {
          const next = new Set(prev)
          next.delete(id)
          return next
        })

        return { success: true, error: null }
      } catch (err) {
        // Rollback optimistic update
        setCards(previousCards)

        // Handle network or unexpected errors
        const errorMessage = 'Brak połączenia z serwerem.'
        setDataError(errorMessage)
        console.error('Unexpected error updating flashcard:', err)

        // Remove from mutating set
        setMutatingIds((prev) => {
          const next = new Set(prev)
          next.delete(id)
          return next
        })

        return { success: false, error: errorMessage }
      }
    },
    [cards]
  )

  /**
   * Delete a single flashcard by ID.
   * Shows spinner for the specific card during deletion.
   *
   * @param {string} id - Flashcard ID to delete
   * @returns {Promise<{success: boolean, error: string|null}>} Operation result
   */
  const remove = useCallback(
    async (id) => {
      // Guard: validate ID format (UUID should be 36 characters)
      if (!id || typeof id !== 'string' || id.length !== 36) {
        return {
          success: false,
          error: 'Nieprawidłowy identyfikator fiszki.',
        }
      }

      // Add ID to mutating set (for spinner)
      setMutatingIds((prev) => new Set(prev).add(id))
      setDataError(null)

      try {
        const { error } = await deleteFlashcard(id)

        if (error) {
          // Transform error to user-friendly message
          const errorMessage = 'Nie udało się usunąć fiszki. Spróbuj ponownie.'
          setDataError(errorMessage)
          console.error('Error deleting flashcard:', error)

          // Remove from mutating set
          setMutatingIds((prev) => {
            const next = new Set(prev)
            next.delete(id)
            return next
          })

          return { success: false, error: errorMessage }
        }

        // Success - refetch to update list
        await fetchFlashcards(false)

        // Remove from mutating set after refetch
        setMutatingIds((prev) => {
          const next = new Set(prev)
          next.delete(id)
          return next
        })

        return { success: true, error: null }
      } catch (err) {
        // Handle network or unexpected errors
        const errorMessage = 'Brak połączenia z serwerem.'
        setDataError(errorMessage)
        console.error('Unexpected error deleting flashcard:', err)

        // Remove from mutating set
        setMutatingIds((prev) => {
          const next = new Set(prev)
          next.delete(id)
          return next
        })

        return { success: false, error: errorMessage }
      }
    },
    [fetchFlashcards]
  )

  /**
   * Delete all flashcards for the current user.
   * Shows loading state during operation.
   *
   * @returns {Promise<{success: boolean, error: string|null}>} Operation result
   */
  const deleteAllCards = useCallback(async () => {
    // Set loading flag
    setIsDeletingAll(true)
    setDataError(null)

    try {
      const { error } = await deleteAllUserFlashcards()

      if (error) {
        // Transform error to user-friendly message
        const errorMessage = 'Nie udało się usunąć fiszek. Spróbuj ponownie.'
        setDataError(errorMessage)
        console.error('Error deleting all flashcards:', error)

        setIsDeletingAll(false)
        return { success: false, error: errorMessage }
      }

      // Success - refetch to update list (should be empty now)
      await fetchFlashcards(false)

      setIsDeletingAll(false)
      return { success: true, error: null }
    } catch (err) {
      // Handle network or unexpected errors
      const errorMessage = 'Brak połączenia z serwerem.'
      setDataError(errorMessage)
      console.error('Unexpected error deleting all flashcards:', err)

      setIsDeletingAll(false)
      return { success: false, error: errorMessage }
    }
  }, [fetchFlashcards])

  // Load flashcards on mount
  useEffect(() => {
    prefetch()
  }, [prefetch])

  return {
    // Data
    cards,

    // Loading states
    isInitialLoading,
    isRefetching,

    // Error state
    dataError,

    // Mutation tracking
    mutatingIds,
    isDeletingAll,

    // Actions
    prefetch,
    insert,
    updateCard,
    remove,
    deleteAllCards,
    refetch,
  }
}
